import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle, Warning } from "@phosphor-icons/react";
import { HELP_AREAS } from "./forms/NeedsForm";
import { INTAKE_GTY_QUESTIONS } from "./forms/GtyIntakeForm";

const YN = ["Yes", "No"];
const MED_SOURCES = ["Client/representative provided", "Current written medication list", "Medication containers reviewed", "Other"];
const DIAGNOSES = ["No known conditions", "Dementia / Alzheimer's", "Diabetes", "Heart disease / CHF", "COPD / breathing problems",
  "Stroke", "Parkinson's", "Arthritis / joint problems", "Depression / anxiety", "Cancer", "Other"];

const optId = (o) => o.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const today = () => new Date().toISOString().slice(0, 10);

function formatPhone(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
const validEmail = (v) => !v || /.+@.+\..+/.test(v);

// detail = inline "checkbox then textbox": picking the trigger value reveals a text input on the same card
const needs = [
  { field: "nickname", q: "First — what does {client} like to be called?", you: "First — what do you like to be called?", type: "text", placeholder: "e.g. everyone calls her Betty" },
  { field: "help_areas", q: "What does {client} need help with? Tap everything that applies — even a rough idea helps.", you: "What do you need help with? Tap everything that applies — even a rough idea helps.", type: "check", options: HELP_AREAS },
  { field: "mobility", q: "How do they usually move around?", you: "How do you usually move around?", type: "radio", options: ["Walks independently", "Walks with a cane or walker", "Uses a wheelchair", "Mostly or completely bedridden"] },
  { field: "transfer_assist", q: "How much physical help do they need to move, stand, or transfer?", you: "How much physical help do you need to move, stand, or transfer?", type: "radio", options: ["No physical help needed", "One-person assist", "Two-person assist", "Not sure"] },
  { field: "memory", q: "Any memory problems?", you: "Do you have any memory problems?", type: "radio", options: ["No memory problems", "Some forgetfulness", "Dementia or significant memory loss"] },
  { field: "description", q: "In your own words — what kind of help matters most day to day?", type: "textarea" },
  { field: "care_goals", q: "What matters most? What should this care accomplish — staying home safely, keeping a routine, recovering strength, companionship...?", you: "What matters most to you? What should this care accomplish — staying home safely, keeping a routine, recovering strength, companionship...?", type: "textarea" },
  { field: "informal_supports", q: "Who else helps out — family, neighbors, church? Names and what they do. (This matters for backup planning.)", you: "Who else helps you out — family, neighbors, church? Names and what they do. (This matters for backup planning.)", type: "textarea" },
  { field: "backup_plan", q: "If a caregiver ever can't make it, what's the backup plan?", you: "If a caregiver ever can't make it, what's your backup plan?", type: "radio",
    options: ["Family or informal support steps in", "Office arranges a backup caregiver", "Skipping the visit is okay", "Not sure — office will ask"] },
];

const goals = [
  { field: "nickname", q: "What does {client} like to be called?", you: "What do you like to be called?", type: "text" },
  { field: "goal", q: "What matters most — what should this care accomplish? (stay home safely, keep routine, recover strength, companionship)", you: "What matters most to you — what should this care accomplish? (stay home safely, keep routine, recover strength, companionship)", type: "textarea" },
  { field: "_informal", q: "Who else helps — family, neighbors, church? Names and what they do.", you: "Who else helps you — family, neighbors, church? Names and what they do.", type: "textarea" },
  { field: "_backup", q: "If a caregiver can't make it, what's the backup plan?", you: "If a caregiver can't make it, what's your backup plan?", type: "radio",
    options: ["Family or informal support steps in", "Office arranges a backup caregiver", "Skipping the visit is okay", "Not sure — office will ask"] },
  { field: "routines_preferences", q: "Any routines or preferences a caregiver should honor? (morning rituals, meals, TV shows, faith practices)", you: "Any routines or preferences a caregiver should honor? (morning rituals, meals, TV shows, faith practices)", type: "textarea", optional: true },
];

const homeVisit = [
  { field: "lives_alone", q: "Does {client} live alone?", you: "Do you live alone?", type: "radio", options: YN,
    detail: { field: "who_lives_in_home", q: "Who else lives there? Names and relationship.", whenValue: "No", multi: true } },
  { field: "under_18", q: "Does anyone under 18 live there or stay over regularly?", you: "Does anyone under 18 live with you or stay over regularly?", type: "radio", options: YN },
  { field: "home_type", q: "What kind of home is it?", you: "What kind of home do you live in?", type: "radio", options: ["Single Family", "Apartment", "Senior Housing", "Other"] },
  { field: "floor_level", q: "Which floor is it on?", type: "radio", options: ["First Floor", "Second Floor", "Other"] },
  { field: "stairs_affecting_care", q: "Will the caregiver need to use stairs?", type: "radio", options: YN },
  { field: "entry_instructions", q: "How should the caregiver get in? Which door, knock or call first, gate codes — anything like that.", type: "textarea" },
  { field: "lockbox_code", q: "Is there a lockbox or door code?", type: "text", optional: true },
  { field: "parking_instructions", q: "Where should the caregiver park?", type: "text", optional: true },
  { field: "pets", q: "Any pets?", you: "Do you have any pets?", type: "radio", options: YN,
    detail: { field: "pet_details", q: "Tell us about them — kind, names, anything the caregiver should know." } },
  { field: "smoking_inside", q: "Does anyone smoke inside the home?", you: "Does anyone smoke inside your home?", type: "radio", options: YN },
  { field: "oxygen_in_use", q: "Is oxygen used in the home?", you: "Is oxygen used in your home?", type: "radio", options: YN,
    detail: { field: "oxygen_details", q: "What equipment, and any precautions?" } },
  { field: "_oxy_smoke_warn", q: "Oxygen and smoking in the same home", type: "warn",
    body: "Oxygen makes fire burn faster and hotter — smoking anywhere in a home with oxygen is a serious fire and explosion risk. This needs a direct conversation with the family before the first visit, and it goes on the safety notes for every caregiver assigned.",
    when: (a) => a.smoking_inside === "Yes" && a.oxygen_in_use === "Yes" },
  { field: "weapons_in_home", q: "Are there weapons or firearms in the home?", you: "Are there weapons or firearms in your home?", type: "radio", options: YN },
  { field: "_weapons_info", q: "About weapons in the home", type: "info",
    body: "That's fine — many of our clients hunt or keep firearms. Our rule for staff safety: they must be stored secured (locked, unloaded) and never kept in the areas where our employees will be working.",
    when: (a) => a.weapons_in_home === "Yes" },
  { field: "weapons_storage", q: "How are they stored, and where? (safe, locked cabinet, which room)", type: "text", when: (a) => a.weapons_in_home === "Yes" },
  { field: "private_rooms", q: "Any rooms or belongings that are off-limits to caregivers?", type: "text", optional: true },
  { field: "other_safety_concern", q: "Any other safety concerns — inside or outside the home?", type: "text", optional: true },
];

const form1 = [
  { field: "full_name", q: "What's {client}'s full name?", you: "What's your full name?", type: "text" },
  { field: "dob", q: "Date of birth?", you: "Your date of birth?", type: "date" },
  { field: "_addr_street", q: "What's the street address where care will happen?", you: "What's the street address where you'll receive care?", type: "text", placeholder: "Street and number" },
  { field: "_addr_city", q: "City?", type: "text" },
  { field: "_addr_state", q: "State?", type: "text", default: "PA" },
  { field: "_addr_zip", q: "ZIP code?", type: "text", mask: "zip" },
  { field: "county", q: "Which county is that?", type: "text" },
  { field: "primary_phone", q: "What's the best phone number?", you: "What's the best phone number to reach you?", type: "tel" },
  { field: "can_text", q: "Is it OK to text that number?", type: "radio", options: YN },
  { field: "email", q: "Email address?", you: "Your email address?", type: "email", optional: true },
  { field: "preferred_communication", q: "How do they prefer we reach them?", you: "How do you prefer we reach you?", type: "radio", options: ["Phone call", "Text", "Email", "Any"] },
  { field: "preferred_language", q: "What language do they prefer?", you: "What language do you prefer?", type: "text", optional: true, placeholder: "English if left blank" },
  { field: "has_legal_rep", q: "Does {client} have someone with legal authority — like a Power of Attorney or guardian?", you: "Do you have someone with legal authority — like a Power of Attorney or guardian?", type: "radio", options: YN },
  { field: "rep_type", q: "What kind?", type: "radio", options: ["POA", "Guardian", "Other"], when: (a) => a.has_legal_rep === "Yes" },
  { field: "rep_name", q: "Their name?", type: "text", when: (a) => a.has_legal_rep === "Yes" },
  { field: "rep_relationship", q: "Their relationship to {client}?", you: "Their relationship to you?", type: "text", when: (a) => a.has_legal_rep === "Yes" },
  { field: "rep_phone", q: "Their phone number?", type: "tel", when: (a) => a.has_legal_rep === "Yes" },
  { field: "rep_email", q: "Their email?", type: "email", optional: true, when: (a) => a.has_legal_rep === "Yes" },
  { field: "directs_own_care", q: "Does {client} make their own care decisions?", you: "Do you make your own care decisions?", type: "radio", options: YN },
  { field: "primary_contact_type", q: "Who should we contact first about care?", you: "Who should we contact first about your care?", type: "radio", options: ["Client", "Authorized Representative", "Other"] },
  { field: "pc_name", q: "What's that person's name?", type: "text", when: (a) => a.primary_contact_type === "Other" },
  { field: "pc_relationship", q: "Their relationship to {client}?", you: "Their relationship to you?", type: "text", when: (a) => a.primary_contact_type === "Other" },
  { field: "pc_phone", q: "Their phone number?", type: "tel", when: (a) => a.primary_contact_type === "Other" },
  { field: "pc_email", q: "Their email?", type: "email", optional: true, when: (a) => a.primary_contact_type === "Other" },
  { field: "ec_name", q: "Who should we call in an emergency?", you: "Who should we call in an emergency for you?", type: "text" },
  { field: "ec_relationship", q: "Their relationship to {client}?", you: "Their relationship to you?", type: "text" },
  { field: "ec_phone", q: "Emergency contact's phone number?", type: "tel" },
  { field: "ec_email", q: "Emergency contact's email?", type: "email", optional: true },
  { field: "payer", q: "Who pays for the care?", you: "Who pays for your care?", type: "radio", options: ["CHC", "Private Pay", "Veterans", "LTC Insurance", "Other"] },
  { field: "mco_name", q: "Which health plan? (UPMC, Keystone First, PA Health & Wellness...)", type: "text", when: (a) => a.payer === "CHC" },
  { field: "medicaid_id", q: "Medicaid ID?", type: "text", optional: true, when: (a) => a.payer === "CHC", placeholder: "Skip if unknown" },
  { field: "mco_member_id", q: "Plan member ID?", type: "text", optional: true, when: (a) => a.payer === "CHC", placeholder: "Skip if unknown" },
  { field: "coordinator_name", q: "Service coordinator's name?", type: "text", optional: true, when: (a) => a.payer === "CHC", placeholder: "Skip if none yet" },
  { field: "coordinator_phone", q: "Service coordinator's phone?", type: "tel", optional: true, when: (a) => a.payer === "CHC" },
  { field: "coordinator_email", q: "Service coordinator's email?", type: "email", optional: true, when: (a) => a.payer === "CHC" },
  { field: "caregiver_referral", q: "Is there a caregiver {client} would like us to consider hiring to provide the care?", you: "Do you have a caregiver you'd like us to consider hiring to provide your care?", type: "radio", options: YN },
  { field: "_referral_info", q: "One legal note about hiring someone you know", type: "info",
    body: "We're glad to consider them — one legal rule first: we cannot employ anyone who is the client's spouse, holds Power of Attorney for the client, serves as the client's legal guardian, or acts as the client's representative payee or any other authorized decision-maker.",
    when: (a) => a.caregiver_referral === "Yes" },
  { field: "referral_name", q: "Their name?", type: "text", when: (a) => a.caregiver_referral === "Yes" },
  { field: "referral_phone", q: "Their phone number?", type: "tel", when: (a) => a.caregiver_referral === "Yes" },
  { field: "referral_email", q: "Their email?", type: "email", optional: true, when: (a) => a.caregiver_referral === "Yes" },
  { field: "referral_notes", q: "Anything else about them — or additional referrals?", type: "textarea", optional: true, when: (a) => a.caregiver_referral === "Yes" },
];

const assessmentHealth = [
  { field: "_dx", q: "Which health conditions affect {client}'s care? Tap everything that applies.", you: "Which health conditions affect your care? Tap everything that applies.", type: "check", options: DIAGNOSES, exclusive: "No known conditions" },
  { field: "_dx_dementia", q: "With the dementia — any wandering, sundowning, or exit-seeking we should plan for?", you: "With the dementia — any wandering or times of confusion we should plan for?", type: "radio", options: YN,
    detail: { field: "_dx_dementia_detail", q: "Tell us what that looks like for them.", you: "Tell us what that looks like for you." }, when: (a) => (a._dx || []).includes("Dementia / Alzheimer's") },
  { field: "_dx_diabetes", q: "Do they use insulin?", you: "Do you use insulin?", type: "radio", options: YN,
    detail: { field: "_dx_diabetes_detail", q: "Who manages it — do they self-administer, family, nursing?", you: "Who manages it — do you self-administer, family, nursing?" }, when: (a) => (a._dx || []).includes("Diabetes") },
  { field: "_dx_stroke", q: "After the stroke — which side is affected? Any speech or swallowing issues?", you: "After the stroke — which side is affected? Any speech or swallowing issues?", type: "text", when: (a) => (a._dx || []).includes("Stroke") },
  { field: "_dx_parkinsons", q: "With the Parkinson's — any freezing episodes, tremors that affect eating, or swallowing concerns?", type: "text", when: (a) => (a._dx || []).includes("Parkinson's") },
  { field: "_dx_other", q: "Which other condition(s)?", type: "text", when: (a) => (a._dx || []).includes("Other") },
  { field: "_food_allergy", q: "Any food allergies?", you: "Do you have any food allergies?", type: "radio", options: YN,
    detail: { field: "_food_allergy_detail", q: "Which foods — and what happens if they eat them?", you: "Which foods — and what happens if you eat them?" } },
  { field: "_med_allergy", q: "Any medication allergies?", you: "Do you have any medication allergies?", type: "radio", options: YN,
    detail: { field: "_med_allergy_detail", q: "Which medications — and what happens if they take them?", you: "Which medications — and what happens if you take them?" } },
  { field: "_exposure_plan", q: "What should staff do to prevent an exposure — and what should happen if one occurs?", type: "textarea",
    when: (a) => a._food_allergy === "Yes" || a._med_allergy === "Yes" },
  { field: "_epi_pen", q: "Do they have an EpiPen?", you: "Do you have an EpiPen?", type: "radio", options: YN,
    detail: { field: "_epi_pen_where", q: "Where is it kept?" },
    when: (a) => a._food_allergy === "Yes" || a._med_allergy === "Yes" },
  { field: "_epi_info", q: "One important note about EpiPens", type: "info",
    body: "Our caregivers are not permitted to administer injections. In an emergency they can hand the client their EpiPen and help them self-administer — and they call 911.",
    when: (a) => a._epi_pen === "Yes" },
  { field: "recent_falls", q: "Any falls in the last year?", you: "Have you had any falls in the last year?", type: "radio", options: YN,
    detail: { field: "fall_details", q: "What caused the fall(s), and what happened — any injuries?" } },
  { field: "recent_hospitalization", q: "Any recent hospital stay, surgery, or procedure?", you: "Have you had a recent hospital stay, surgery, or procedure?", type: "radio", options: YN,
    detail: { field: "hospitalization_details", q: "What was it for, and when?" } },
  { field: "mobility_status", q: "How do they get around day to day? (walks alone, cane, walker, wheelchair, bedbound)", you: "How do you get around day to day? (walk alone, cane, walker, wheelchair, bedbound)", type: "text" },
  { field: "assistive_devices", q: "Any other equipment in the home they use — commode, hospital bed, grab bars, lift? (Oxygen's already covered.)", you: "Any other equipment in your home you use — commode, hospital bed, grab bars, lift? (Oxygen's already covered.)", type: "text", optional: true },
  { field: "cognitive_concerns", q: "Any memory or thinking problems that affect care?", you: "Do you have any memory or thinking problems that affect your care?", type: "radio", options: YN,
    detail: { field: "cognitive_details", q: "Tell us a little about it." } },
  { field: "behavioral_concerns", q: "Any behaviors or mental-health concerns that affect care — agitation, wandering, anxiety, depression, loneliness?", you: "Anything affecting how you feel day to day — anxiety, depression, loneliness?", type: "radio", options: YN,
    detail: { field: "behavioral_details", q: "Tell us a little about it." } },
  { field: "communication_limitations", q: "Glasses, hearing aids, dentures — or any trouble with hearing, vision, or speech?", you: "Do you use glasses, hearing aids, or dentures — or have any trouble with hearing, vision, or speech?", type: "radio", options: YN,
    detail: { field: "communication_details", q: "Which ones, and anything to know (like 'hearing aid, left ear' or 'dentures come out at night')." } },
  { field: "other_health_info", q: "Anything else about their health we should know?", you: "Anything else about your health we should know?", type: "textarea", optional: true },
  { field: "physician_name", q: "Their primary doctor's name?", you: "Your primary doctor's name?", type: "text" },
  { field: "physician_phone", q: "The doctor's phone number?", type: "tel" },
  { field: "hospice", q: "Is hospice involved?", type: "radio", options: YN,
    detail: { field: "hospice_provider", q: "Which hospice provider?" } },
  { field: "skilled_nursing", q: "Is a skilled nursing or wound care service involved?", type: "radio", options: YN,
    detail: { field: "skilled_nursing_provider", q: "Which provider?" } },
];

const assessmentOffice = [
  { field: "assessment_date", q: "Assessment date?", type: "date" },
  { field: "completed_by", q: "Completed by?", type: "text" },
  { field: "assessment_type", q: "Assessment type?", type: "radio", options: ["New admission", "Reassessment", "Significant change"] },
  ...homeVisit,
  ...assessmentHealth,
];

const gty = [
  ...INTAKE_GTY_QUESTIONS.map((q) => ({ field: q.id, q: q.text, type: "textarea" })),
  { field: "anything_else", q: "Anything else that would help a caregiver feel like they know them?", type: "textarea" },
];

const SECTIONS = {
  needs: { title: "What they need help with", questions: needs },
  home_visit: { title: "Their home", questions: homeVisit,
    compose: (a) => {
      const out = { ...a };
      if (a.smoking_inside === "Yes" && a.oxygen_in_use === "Yes") {
        out.other_safety_concern = [a.other_safety_concern, "OXYGEN + SMOKING IN THE HOME — fire risk flagged at intake; address with family before first visit."].filter(Boolean).join(" | ");
      }
      return out;
    } },
  form1: { title: "The important details", questions: form1,
    compose: (a) => {
      const out = { ...a };
      const parts = [a._addr_street, a._addr_city, `${a._addr_state || ""} ${a._addr_zip || ""}`.trim()].filter(Boolean);
      out.service_address = parts.join(", ");
      return out;
    } },
  goals: { title: "Goals & backup plan", questions: goals,
    compose: (a) => {
      const out = { ...a };
      const bits = [];
      if (a._backup) bits.push(`Backup plan: ${a._backup}`);
      if (a._informal) bits.push(`Informal supports: ${a._informal}`);
      if (bits.length) out.backup_instructions = bits.join(". ");
      if (a.nickname) out.verbal_summary = `Client prefers to be called ${a.nickname}.`;
      return out;
    } },
  assessment: { title: "Health & safety", questions: assessmentHealth, compose: composeAssessment },
  assessment_office: { title: "Assessment", questions: assessmentOffice, compose: composeAssessment },
  gty: { title: "Getting to know them", questions: gty },
  medications: { title: "Medications", questions: [] },
};

function composeAssessment(a) {
  const out = { ...a };
  const dx = (a._dx || []).filter((d) => d !== "No known conditions");
  const bits = [...dx];
  if (a._dx_dementia === "Yes") bits.push(`dementia: wandering/sundowning — ${a._dx_dementia_detail || "yes"}`);
  if (a._dx_diabetes === "Yes") bits.push(`diabetes: insulin — ${a._dx_diabetes_detail || "yes"}`);
  if (a._dx_stroke) bits.push(`stroke: ${a._dx_stroke}`);
  if (a._dx_parkinsons) bits.push(`parkinson's: ${a._dx_parkinsons}`);
  if (a._dx_other) bits.push(`other: ${a._dx_other}`);
  out.primary_diagnoses = bits.join("; ") || (a._dx || []).join("; ") || "None known";
  const allergyBits = [];
  if (a._food_allergy === "Yes") allergyBits.push(`food: ${a._food_allergy_detail || "yes"}`);
  if (a._med_allergy === "Yes") allergyBits.push(`medication: ${a._med_allergy_detail || "yes"}`);
  if (a._exposure_plan) allergyBits.push(`exposure plan: ${a._exposure_plan}`);
  if (a._epi_pen === "Yes") allergyBits.push(`EpiPen kept: ${a._epi_pen_where || "yes"} (staff may assist self-administration only, call 911)`);
  out.allergies = allergyBits.length ? "Yes" : "None";
  out.allergy_details = allergyBits.join("; ");
  return out;
}

function splitAddress(addr) {
  const m = { _addr_street: addr || "", _addr_city: "", _addr_state: "PA", _addr_zip: "" };
  const parts = String(addr || "").split(",").map((s) => s.trim());
  if (parts.length >= 3) {
    m._addr_street = parts[0];
    m._addr_city = parts[1];
    const st = parts[2].match(/([A-Za-z]{2})\s*(\d{5})?/);
    if (st) { m._addr_state = st[1] || "PA"; m._addr_zip = st[2] || ""; }
  }
  return m;
}

// one coherent demo story: 82-year-old woman, apartment 2B, walker, cat Mabel,
// daughter Susan is POA, diabetes + arthritis, hard of hearing, Keystone First CHC
const DEMO_STORY = {
  // needs
  nickname: "Ellie",
  help_areas: ["Meal preparation", "Housekeeping & laundry", "Rides / transportation", "Medication reminders", "Companionship"],
  mobility: "Walks with a cane or walker",
  transfer_assist: "No physical help needed",
  memory: "Some forgetfulness",
  description: "Mom is 82 and gets around her apartment fine with her walker. Since Dad passed she's lonely and stopped cooking real meals. Main needs are lunch and dinner prep, light housekeeping, a ride to appointments on Tuesdays, and someone to remind her about evening medications.",
  care_goals: "Keeping her safe in her own apartment — that's the whole goal. Regular meals, a clean place, and somebody to talk to so the days don't feel so long.",
  informal_supports: "Daughter Susan (POA) visits every Sunday and handles bills. Granddaughter Katie calls each evening. Neighbor Ruth checks in most mornings.",
  backup_plan: "Family or informal support steps in",
  // home
  lives_alone: "Yes", under_18: "No", home_type: "Apartment", floor_level: "Second Floor", stairs_affecting_care: "Yes",
  entry_instructions: "Building buzzer code 4521, unit 2B. Knock loudly — she's hard of hearing.",
  lockbox_code: "", parking_instructions: "Visitor spots in the rear lot",
  pets: "Yes", pet_details: "One elderly cat, Mabel — friendly, please don't let her out",
  smoking_inside: "No", oxygen_in_use: "No", weapons_in_home: "No",
  private_rooms: "The bedroom filing cabinet is private",
  other_safety_concern: "Loose rug in the hallway is a trip hazard",
  // details
  dob: "1944-01-28", _addr_street: "88 Maple St", _addr_city: "Scranton", _addr_state: "PA", _addr_zip: "18505",
  county: "Lackawanna", primary_phone: "(570) 555-0102", can_text: "Yes", email: "susan.ellsworth@example.com",
  preferred_communication: "Phone call", preferred_language: "English",
  has_legal_rep: "Yes", rep_type: "POA", rep_name: "Susan Ellsworth", rep_relationship: "Daughter",
  rep_phone: "(570) 555-0113", rep_email: "susan.ellsworth@example.com",
  directs_own_care: "Yes", primary_contact_type: "Authorized Representative",
  ec_name: "Susan Ellsworth", ec_relationship: "Daughter", ec_phone: "(570) 555-0113", ec_email: "susan.ellsworth@example.com",
  payer: "CHC", mco_name: "Keystone First CHC", medicaid_id: "PA-88213467", mco_member_id: "KF-554210",
  coordinator_name: "Dana Whitfield", coordinator_phone: "(570) 555-0177", coordinator_email: "dana.whitfield@mco.example.com",
  caregiver_referral: "No",
  // health & safety
  _dx: ["Diabetes", "Arthritis / joint problems"],
  _dx_diabetes: "No",
  _food_allergy: "No",
  _med_allergy: "Yes", _med_allergy_detail: "Penicillin — it gives her hives",
  _exposure_plan: "No penicillin-based medications in the home. If she's ever exposed, call 911 first, then Susan.",
  _epi_pen: "No",
  recent_falls: "Yes", fall_details: "Tripped on the hallway rug last spring — no injury, and the rug has been taped down since",
  recent_hospitalization: "No",
  mobility_status: "Walks with a walker inside the apartment; cane for short trips",
  assistive_devices: "Walker, cane, grab bars in the bathroom",
  cognitive_concerns: "Yes", cognitive_details: "Some forgetfulness — she needs a reminder for evening medications",
  behavioral_concerns: "Yes", behavioral_details: "Gets lonely and a little anxious since her husband passed; she does best with a steady routine",
  communication_limitations: "Yes", communication_details: "Hard of hearing in the left ear — speak on her right side. Glasses for reading; dentures come out at night.",
  other_health_info: "She's fiercely independent — offer choices rather than doing things for her",
  physician_name: "Dr. Alvarez", physician_phone: "(570) 555-0143",
  hospice: "No", skilled_nursing: "No",
  // getting to know them
  intake_gty_day: "Coffee on the porch, watching her soaps at 1pm, and a phone call with her granddaughter in the evening.",
  intake_gty_food: "Loves Italian food and butterscotch. Hates cilantro. Soft foods preferred — dentures.",
  intake_gty_routine: "Mass on TV every Sunday morning. Nap from 2 to 3pm — please don't schedule anything then.",
  intake_gty_interests: "Gardening (she had a huge rose garden), crossword puzzles, her late husband's naval service.",
  intake_gty_upsets: "Being rushed. Loud voices. Talk of nursing homes — she's afraid of leaving her apartment.",
  intake_gty_people: "Daughter Susan (POA) visits Sundays. Granddaughter Katie calls every evening. Cat Mabel.",
  intake_gty_communication: "Hard of hearing in the left ear — speak on her right side. Glasses for reading.",
  anything_else: "She responds really well to humor and gentle teasing.",
};

// second-person wording for Getting-to-know-you when the client answers themselves
const GTY_YOU = {
  intake_gty_day: "What does a good day look like for you?",
  intake_gty_food: "Your favorite foods — and any foods to avoid?",
  intake_gty_routine: "Daily routines that matter to you (morning rituals, TV shows, nap times, faith practices)?",
  intake_gty_interests: "Hobbies, interests, or topics you love to talk about?",
  intake_gty_upsets: "Is there anything that upsets, worries, or frustrates you?",
  intake_gty_people: "Important people, pets, or traditions in your life?",
  intake_gty_communication: "How do you communicate best? Any hearing, vision, or language needs?",
  anything_else: "Anything else that would help a caregiver feel like they know you?",
};

function demoValueFor(q) {
  if (q.field === "full_name") return ""; // never fake the client's name — it poisons personalization and the record
  if (q.field in DEMO_STORY && DEMO_STORY[q.field] !== "") return DEMO_STORY[q.field];
  if (q.optional) return "";
  if (q.type === "radio") return q.options[0];
  if (q.type === "check") return q.exclusive ? [q.options[1]] : q.options.slice(0, 2);
  if (q.type === "date") return today();
  if (q.type === "tel") return "(570) 555-0102";
  if (q.type === "email") return "susan.ellsworth@example.com";
  if (q.type === "textarea") return "Nothing else comes to mind";
  return "Not sure — the office will follow up";
}

export default function IntakeWizard({ sectionKey, mode = "family", audience = "family", initial = {}, clientName, gtyQuestions, askFiller = false, welcome = false, demo = false, onUploadFile, onSave, onDone }) {
  const key = sectionKey === "assessment" && mode === "office" ? "assessment_office" : sectionKey;
  const gtyList = gtyQuestions || INTAKE_GTY_QUESTIONS;
  const isClient = audience === "client";
  const section = key === "gty"
    ? { ...SECTIONS.gty, questions: [...gtyList.map((q) => ({ field: q.id, q: (isClient && GTY_YOU[q.id]) || q.text, type: "textarea" })), { field: "anything_else", q: (isClient && GTY_YOU.anything_else) || "Anything else that would help a caregiver feel like they know them?", type: "textarea" }] }
    : SECTIONS[key];

  const [answers, setAnswers] = useState(() => {
    let base;
    if (key === "gty") {
      base = {};
      for (const q of gtyList) base[q.id] = initial.guided_answers?.[q.id]?.answer || "";
      base.anything_else = initial.anything_else || "";
    } else if (key === "medications") {
      base = { has_meds: (initial.entries || []).some((e) => e.name) ? "Yes" : "", entries: (initial.entries || []).filter((e) => e.name), source: initial.source || "", source_other: initial.source_other || "", notes: initial.notes || "" };
    } else if (key === "form1") {
      base = { full_name: clientName && clientName !== "the client" ? clientName : "", ...initial, ...splitAddress(initial.service_address) };
    } else {
      base = { ...initial };
    }
    return { _filled_by: "", _filled_role: "", ...base };
  });
  const [introDone, setIntroDone] = useState(!askFiller || demo);
  const [welcomed, setWelcomed] = useState(!welcome || demo);
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [medStep, setMedStep] = useState(0);
  const [draftMed, setDraftMed] = useState({ name: "", dosage: "", frequency: "" });
  const focusRef = useRef(null);

  const displayName = answers.full_name?.trim() || clientName || "the client";
  const fillerName = (answers._filled_by || "").trim();
  const filler = fillerName + (answers._filled_role ? ` · ${answers._filled_role}` : "");
  // audience-aware question text: explicit "you" variant first, then name substitution
  const personalize = (s) => {
    let out = (s || "").replaceAll("{client}'s", isClient ? "your" : `${displayName}'s`).replaceAll("{client}", isClient ? "you" : displayName);
    return out;
  };
  const qt = (q) => (isClient && q.you) ? personalize(q.you) : personalize(q.q);

  const visible = useMemo(
    () => (section.questions || []).filter((q) => !q.when || q.when(answers)),
    [section, answers]
  );

  // latest-state ref so delayed callbacks (radio auto-advance) never act on stale answers/visible
  const latest = useRef({ visible, idx: 0, answers });
  useEffect(() => { latest.current = { visible, idx, answers }; });

  // default empty dates to today + autofocus on each new question
  const currentField = key === "medications" ? null : visible[idx]?.field;
  useEffect(() => {
    if (key === "medications") return;
    const q = visible[idx];
    if (!q) return;
    if (q.type === "date" && !answers[q.field]) setAnswers((cur) => ({ ...cur, [q.field]: today() }));
    if (q.default && !answers[q.field]) setAnswers((cur) => ({ ...cur, [q.field]: q.default }));
    // demo walk-through: each question appears already filled; user just taps Continue
    if (demo && q.type !== "warn" && q.type !== "info") {
      const curVal = answers[q.field];
      const isEmpty = Array.isArray(curVal) ? curVal.length === 0 : !String(curVal ?? "").trim();
      if (isEmpty) setAnswers((cur) => ({ ...cur, [q.field]: demoValueFor(q) }));
      const d = q.detail;
      if (d) {
        const shown = (answers[q.field] ?? demoValueFor(q)) === (d.whenValue || "Yes");
        if (shown && !String(answers[d.field] ?? "").trim()) {
          setAnswers((cur) => ({ ...cur, [d.field]: DEMO_STORY[d.field] || "Demo detail" }));
        }
      }
    }
    const t = setTimeout(() => {
      focusRef.current?.querySelector("input, textarea")?.focus();
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentField, idx, key]);

  // demo walk-through for the medications flow: prefill fields as they appear
  useEffect(() => {
    if (!demo || key !== "medications") return;
    if (medStep === 1 && !draftMed.name) setDraftMed((d) => ({ ...d, name: "Lisinopril" }));
    if (medStep === 2 && !draftMed.dosage) setDraftMed((d) => ({ ...d, dosage: "10 mg" }));
    if (medStep === 3 && !draftMed.frequency) setDraftMed((d) => ({ ...d, frequency: "once a day" }));
    if (medStep === 7 && !answers.notes) setAnswers((cur) => ({ ...cur, notes: "Demo list" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medStep, demo, key]);

  const stripInternal = (a) => Object.fromEntries(Object.entries(a).filter(([k]) => !k.startsWith("_")));

  const finish = async (raw) => {
    setSaving(true);
    try {
      let payload = section.compose ? section.compose(raw) : raw;
      payload = stripInternal(payload);
      if (fillerName && key === "needs") {
        payload.filled_by = fillerName + (raw._filled_role ? ` (${raw._filled_role})` : "");
      }
      if (key === "gty") {
        const guided = {};
        for (const q of gtyList) {
          if ((payload[q.id] || "").trim()) guided[q.id] = { question: q.text, answer: payload[q.id].trim() };
        }
        payload = { guided_answers: guided, anything_else: payload.anything_else || "" };
      }
      await onSave(payload);
      onDone?.();
    } finally {
      setSaving(false);
    }
  };

  const setA = (field, value) => setAnswers((cur) => ({ ...cur, [field]: value }));

  const fillDemo = () => {
    if (key === "medications") {
      setAnswers((cur) => ({ ...cur, has_meds: "Yes", entries: [{ name: "Lisinopril", dosage: "10 mg", frequency: "once a day" }, { name: "Vitamin D3", dosage: "1000 IU", frequency: "morning" }], source: MED_SOURCES[0], notes: "Demo list" }));
      return;
    }
    const filled = {};
    for (const q of visible) { if (q.field && q.type !== "warn" && q.type !== "info") filled[q.field] = demoValueFor(q); }
    setAnswers((cur) => ({ ...cur, ...filled, ...DEMO_STORY }));
  };

  // ---- welcome screen ----
  if (!welcomed) {
    const family = mode === "family";
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10" data-testid="wizard-welcome">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Silver Home Care</p>
        <h2 className="mt-2 font-heading text-2xl font-semibold text-stone-900 sm:text-3xl">
          {family ? (isClient ? "Welcome — this intake is about you." : `Welcome${displayName !== "the client" ? ` — this is about ${displayName}` : ""}.`) : "Guided intake — staff mode."}
        </h2>
        {family ? (
          <div className="mt-4 space-y-3 text-base leading-relaxed text-stone-600">
            <p>We'll ask the intake questions <strong>one at a time</strong> — no long forms. Most people finish in about 10 minutes.</p>
            <p>Answer what you know and skip what you don't. Everything saves as you go, so you can stop and come back to this link later.</p>
            <p>When the questions are done, the documents will be ready to review and sign — that's the only signing you'll need to do.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3 text-base leading-relaxed text-stone-600">
            <p><strong>How to run this:</strong> read each question aloud, exactly as written, and tap the answer they give you.</p>
            <p>If they don't know something, use <strong>Skip</strong> — don't guess. Every section saves when you finish it, so you can pause anytime.</p>
            <p>At the end you'll get the signature step: <strong>sign right on this device</strong>, or <strong>email the signing link</strong> to the client or family.</p>
          </div>
        )}
        <button type="button" onClick={() => setWelcomed(true)}
          className="mt-7 inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-all hover:bg-stone-900"
          data-testid="wizard-welcome-start">
          Let's begin <ArrowRight size={17} />
        </button>
      </div>
    );
  }

  // ---- intro: who's filling this out — role first, then name ----
  if (!introDone) {
    const [step1Done, role] = [!!answers._filled_role, (answers._filled_role || "")];
    if (!step1Done) {
      return (
        <WizardShell title={section.title} cur={0} total={key === "medications" ? 3 : visible.length} filler=""
          onBack={null} onSkip={null} onNext={null} nextLabel="" saving={false} nextDisabled={false}>
          <QuestionText q={mode === "office" ? "First — who is completing this intake? Your role." : "First — who's filling this out? Your role."} step={0} />
          <div ref={focusRef}>
            <RadioBig value={answers._filled_role || ""}
              options={["Family member", "Friend or neighbor", "Legal representative (POA / guardian)", "The client themselves", "Office staff", "Other"]}
              onPick={(v) => setA("_filled_role", v)} tid="wizard-filled-role" />
          </div>
        </WizardShell>
      );
    }
    const canGo = filler.length > 0;
    return (
      <WizardShell title={section.title} cur={0} total={key === "medications" ? 3 : visible.length} filler=""
        onBack={() => setA("_filled_role", "")} onSkip={null} onNext={() => canGo && setIntroDone(true)} nextLabel="Let's start" saving={false} nextDisabled={!canGo}>
        <QuestionText q={`${answers._filled_role === "Other" ? "And your name?" : `${answers._filled_role} — and your name?`}`} step={0} />
        <div ref={focusRef}>
          <TextBig value={answers._filled_by} placeholder="Your name" onChange={(v) => setA("_filled_by", v)} onEnter={() => canGo && setIntroDone(true)} tid="wizard-filled-by" />
        </div>
        <p className="mt-3 text-xs text-stone-400">We'll address the questions to you, so it feels like a conversation instead of paperwork.</p>
      </WizardShell>
    );
  }

  // ---- medications special flow ----
  if (key === "medications") {
    const meds = answers.entries || [];
    const uploaded = !!answers._med_file;

    const medSave = () => finish({
      date_reviewed: today(), entries: meds,
      notes: answers.notes || "",
      source: uploaded ? "Uploaded medication list (photo/file)" : (answers.source || ""),
      source_other: answers.source_other || "",
    });

    const doUpload = async (file) => {
      if (!file || !onUploadFile) return;
      setSaving(true);
      try {
        await onUploadFile(file);
        setAnswers((cur) => ({ ...cur, _med_file: file.name }));
        setMedStep(7);
      } finally {
        setSaving(false);
      }
    };

    let body;
    if (medStep === 0) {
      body = <RadioBig value={answers.has_meds} options={YN} onPick={(v) => { setA("has_meds", v); setMedStep(v === "Yes" ? "method" : 5); }} tid="wizard-med-has" />;
    } else if (medStep === "method") {
      body = <RadioBig value="" options={["Take a photo / upload the list", "Type them one at a time"]} onPick={(v) => {
        if (v.startsWith("Take")) setMedStep("upload");
        else { setDraftMed({ name: "", dosage: "", frequency: "" }); setMedStep(1); }
      }} tid="wizard-med-method" />;
    } else if (medStep === "upload") {
      body = (
        <div className="space-y-3" data-testid="wizard-med-upload">
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 px-6 py-10 text-center transition-colors hover:border-primary hover:bg-primary/5">
            <span className="text-base font-medium text-stone-700">{saving ? "Uploading..." : "Tap to take a photo or choose a file"}</span>
            <span className="mt-1 text-xs text-stone-400">Photo of the med list, pill bottles, or a pharmacy printout</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" disabled={saving}
              onChange={(e) => doUpload(e.target.files?.[0])} data-testid="wizard-med-file" />
          </label>
          <button type="button" onClick={() => setMedStep(1)} className="text-xs font-medium text-primary hover:underline" data-testid="wizard-med-type-instead">
            Or type them one at a time instead
          </button>
        </div>
      );
    } else if (medStep <= 3) {
      const fields = ["name", "dosage", "frequency"];
      const f = fields[medStep - 1];
      body = (
        <div ref={focusRef}>
          <TextBig value={draftMed[f]} mask={f === "name" ? null : null} placeholder="Type the answer here"
            onChange={(v) => setDraftMed((d) => ({ ...d, [f]: v }))}
            onEnter={() => { if (draftMed[f].trim() || medStep > 1) setMedStep(medStep + 1); }}
            tid={`wizard-med-${f}`} />
        </div>
      );
    } else if (medStep === 4) {
      body = <RadioBig value="" options={YN} onPick={(v) => {
        if (v === "Yes") { setDraftMed({ name: "", dosage: "", frequency: "" }); setMedStep(1); }
        else setMedStep(5);
      }} tid="wizard-med-more" />;
    } else if (medStep === 5) {
      body = <RadioBig value={answers.source} options={MED_SOURCES} onPick={(v) => { setA("source", v); setMedStep(v === "Other" ? 6 : 7); }} tid="wizard-med-source" />;
    } else if (medStep === 6) {
      body = <div ref={focusRef}><TextBig value={answers.source_other} placeholder="Where did the list come from?" onChange={(v) => setA("source_other", v)} onEnter={() => setMedStep(7)} tid="wizard-med-source-other" /></div>;
    } else {
      body = <div ref={focusRef}><TextBig value={answers.notes} placeholder="Anything else about the medications? (skip if nothing)" onChange={(v) => setA("notes", v)} onEnter={medSave} tid="wizard-med-notes" /></div>;
    }

    const medQ = medStep === 0 ? (isClient ? "Do you take any medications, vitamins, or supplements — including over-the-counter ones?" : `Does ${displayName} take any medications, vitamins, or supplements — including over-the-counter ones?`)
      : medStep === "method" ? "How do you want to share the list?"
      : medStep === "upload" ? "Upload the medication list"
      : medStep === 1 ? (meds.length ? "Next medication — name?" : "Medication name?")
      : medStep === 2 ? "Dosage / strength? (skip if unsure)"
      : medStep === 3 ? "How often is it taken?"
      : medStep === 4 ? "Add another medication?"
      : medStep === 5 ? "Where did this medication list come from?"
      : medStep === 6 ? "What source, then?"
      : uploaded ? `Got it — the list is attached${answers._med_file ? ` (${answers._med_file})` : ""}. Any medication notes? (like 'keep with morning pills')`
      : "Any medication notes? (like 'keep with morning pills')";

    const medSeq = uploaded ? [0, "method", "upload", 7] : answers.has_meds === "Yes" ? [0, "method", 1, 2, 3, 4, 5, 6, 7] : [0, 5, 7];
    const seqIdx = Math.max(0, medSeq.indexOf(medStep));
    const curNum = seqIdx + 1;
    const total = medSeq.length;
    const canContinue = medStep === 1 ? (!!draftMed.name.trim() || meds.length > 0) : medStep === "upload" ? false : true;
    const skipAllowed = medStep === 7 || medStep === 6 || medStep === 2 || medStep === 3;

    const medBack = () => {
      if (medStep === "method" || medStep === 5) setMedStep(0);
      else if (medStep === "upload" || medStep === 1) setMedStep("method");
      else if (medStep === 7) setMedStep(uploaded ? "upload" : answers.has_meds === "No" ? 0 : meds.length ? 4 : 1);
      else setMedStep(medSeq[seqIdx - 1] ?? 0);
    };

    return (
      <WizardShell title={section.title} cur={curNum} total={total} filler={filler} demoFill={demo ? fillDemo : null}
        onBack={medStep !== 0 ? medBack : null}
        onSkip={skipAllowed ? (medStep === 7 ? medSave : () => setMedStep(medStep + 1)) : null}
        onNext={medStep === 7 ? medSave : medStep >= 1 && medStep <= 3 ? () => {
          if (medStep === 1 && !draftMed.name.trim() && meds.length) { setMedStep(5); return; }
          if (medStep === 3) { setAnswers((cur) => ({ ...cur, entries: [...(cur.entries || []), draftMed] })); setMedStep(4); }
          else setMedStep(medStep + 1);
        } : null}
        nextLabel={medStep === 7 ? "Save & continue" : "Continue"} saving={saving} nextDisabled={!canContinue}>
        <QuestionText q={medQ} step={medStep} />
        {meds.length > 0 && medStep >= 1 && medStep <= 4 && (
          <div className="mb-4 flex flex-wrap gap-1.5" data-testid="wizard-med-list">
            {meds.map((m, i) => <span key={i} className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/5 px-2.5 py-1 text-xs text-[#1e8449]">{m.name}</span>)}
          </div>
        )}
        {body}
      </WizardShell>
    );
  }

  // ---- standard question flow ----
  const q = visible[idx];
  if (!q) {
    // visible list can shrink when when() conditions react to answers — clamp and re-render
    setIdx(Math.max(0, visible.length - 1));
    return null;
  }
  const val = answers[q.field] ?? "";
  const optional = !!q.optional;
  const isCard = q.type === "warn" || q.type === "info";
  const detailShown = q.detail && val === (q.detail.whenValue || "Yes");
  const detailVal = detailShown ? (answers[q.detail.field] ?? "") : "";
  const emailBad = q.type === "email" && !validEmail(val);
  const canNext = isCard || ((optional || (Array.isArray(val) ? val.length > 0 : String(val || "").trim() !== ""))
    && (!detailShown || String(detailVal).trim() !== "") && !emailBad);
  const last = idx === visible.length - 1;

  const next = () => {
    if (!canNext) return;
    if (last) finish(answers);
    else setIdx(idx + 1);
  };

  const pick = (v) => {
    setA(q.field, v);
    if (q.detail && v === (q.detail.whenValue || "Yes")) return; // wait for the inline detail
    setTimeout(() => {
      // recompute against fresh state: changing an answer can reveal conditional questions,
      // so "last" and the finish payload must not come from the stale render
      const cur = latest.current;
      const na = { ...cur.answers, [q.field]: v };
      const nv = (section.questions || []).filter((qq) => !qq.when || qq.when(na));
      const at = cur.visible.findIndex((qq) => qq.field === q.field);
      if (at >= 0 && at === nv.length - 1) finish(na);
      else setIdx(cur.idx + 1);
    }, 180);
  };

  const toggleCheck = (o) => {
    const cur = val || [];
    if (q.exclusive && o === q.exclusive) { setA(q.field, cur.includes(o) ? [] : [o]); return; }
    const cleared = q.exclusive ? cur.filter((x) => x !== q.exclusive) : cur;
    setA(q.field, cleared.includes(o) ? cleared.filter((x) => x !== o) : [...cleared, o]);
  };

  return (
    <WizardShell title={section.title} cur={idx + 1} total={visible.length} filler={filler} demoFill={demo ? fillDemo : null}
      onBack={idx > 0 ? () => setIdx(idx - 1) : () => setIntroDone(false)}
      onSkip={optional ? next : null}
      onNext={next} nextLabel={last ? "Save & continue" : "Continue"} saving={saving} nextDisabled={!canNext}>
      {q.type === "warn" ? (
        <div className="rounded-lg border-2 border-[#C0392B]/50 bg-[#C0392B]/5 p-5" data-testid="wizard-warn">
          <div className="flex items-start gap-3">
            <Warning size={26} weight="fill" className="mt-0.5 shrink-0 text-[#C0392B]" />
            <div>
              <p className="font-heading text-lg font-semibold text-[#C0392B]">{qt(q)}</p>
              <p className="mt-2 text-sm leading-relaxed text-stone-700">{q.body}</p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[#C0392B]">Press Continue to acknowledge</p>
            </div>
          </div>
        </div>
      ) : q.type === "info" ? (
        <div className="rounded-lg border border-[#8a5d14]/40 bg-[#8a5d14]/5 p-5" data-testid="wizard-info">
          <p className="font-heading text-lg font-semibold text-[#8a5d14]">{qt(q)}</p>
          <p className="mt-2 text-sm leading-relaxed text-stone-700">{q.body}</p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[#8a5d14]">Press Continue to acknowledge</p>
        </div>
      ) : (
        <>
          <QuestionText q={qt(q)} step={idx + 1} />
          <div ref={focusRef}>
            {q.type === "radio" && <RadioBig value={val} options={q.options} onPick={pick} tid={`wizard-opt-${q.field}`} />}
            {q.type === "check" && <CheckBig values={val || []} options={q.options} onToggle={toggleCheck} tid={`wizard-chk-${q.field}`} />}
            {(q.type === "text" || q.type === "tel" || q.type === "email" || q.type === "date") && (
              <>
                <TextBig value={val} type={q.type === "tel" ? "tel" : q.type} placeholder={q.placeholder || "Type the answer here"}
                  onChange={(v) => setA(q.field, q.type === "tel" ? formatPhone(v) : v)} onEnter={next} tid={`wizard-input-${q.field}`} />
                {emailBad && <p className="mt-2 text-xs font-medium text-[#C0392B]" data-testid="wizard-email-error">That email doesn't look right — check for the @ and domain.</p>}
              </>
            )}
            {q.type === "textarea" && <AreaBig value={val} onChange={(v) => setA(q.field, v)} tid={`wizard-area-${q.field}`} />}
            {detailShown && (
              <div className="mt-4 border-l-4 border-primary/40 pl-4" data-testid={`wizard-detail-${q.field}`}>
                <p className="mb-2 text-sm font-medium text-stone-700">{qt(q.detail)}</p>
                <TextBig value={detailVal} small placeholder="Type the detail here"
                  onChange={(v) => setA(q.detail.field, v)} onEnter={next} tid={`wizard-detail-input-${q.field}`} />
              </div>
            )}
          </div>
        </>
      )}
    </WizardShell>
  );
}

function WizardShell({ title, cur, total, filler, onBack, onSkip, onNext, nextLabel, saving, nextDisabled, demoFill, children }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8" data-testid="intake-wizard">
      <div className="mb-1 flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">{title}{filler ? ` · with ${filler}` : ""}</p>
        <div className="flex items-center gap-3">
          {demoFill && (
            <button type="button" onClick={demoFill} className="text-[11px] font-semibold text-[#8a5d14] hover:underline" data-testid="wizard-demo-fill">
              Fill with demo data
            </button>
          )}
          {cur > 0 && <p className="text-xs text-stone-400" data-testid="wizard-progress">Question {cur} of {total}</p>}
        </div>
      </div>
      <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-stone-100">
        <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${total ? (cur / total) * 100 : 0}%` }} />
      </div>
      {children}
      <div className="mt-8 flex items-center justify-between">
        <div>
          {onBack && (
            <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-stone-400 hover:text-stone-700" data-testid="wizard-back">
              <ArrowLeft size={15} /> Back
            </button>
          )}
        </div>
        <div className="flex items-center gap-4">
          {onSkip && <button type="button" onClick={onSkip} className="text-sm text-stone-400 hover:text-stone-600" data-testid="wizard-skip">Skip this one</button>}
          {onNext && (
            <button type="button" onClick={onNext} disabled={saving || nextDisabled}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-stone-900 disabled:opacity-40"
              data-testid="wizard-next">
              {saving ? "Saving..." : nextLabel} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionText({ q }) {
  return <p className="mb-5 font-heading text-xl font-medium leading-snug text-stone-900 sm:text-2xl" data-testid="wizard-question">{q}</p>;
}

function RadioBig({ value, options, onPick, tid }) {
  return (
    <div className="grid gap-2.5" data-testid={tid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onPick(o)} data-testid={`${tid}-${optId(o)}`}
          className={`flex items-center justify-between rounded-lg border px-4 py-3.5 text-left text-base transition-all ${
            value === o ? "border-primary bg-primary/10 font-medium text-primary" : "border-stone-200 bg-white text-stone-600 hover:border-stone-400 hover:text-stone-900"
          }`}>
          {o}
          {value === o && <CheckCircle size={18} weight="fill" className="shrink-0" />}
        </button>
      ))}
    </div>
  );
}

function CheckBig({ values, options, onToggle, tid }) {
  return (
    <div className="grid gap-2.5 sm:grid-cols-2" data-testid={tid}>
      {options.map((o) => {
        const on = (values || []).includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`${tid}-${optId(o)}`}
            className={`flex items-center justify-between rounded-lg border px-4 py-3 text-left text-sm transition-all ${
              on ? "border-primary bg-primary/10 font-medium text-primary" : "border-stone-200 bg-white text-stone-600 hover:border-stone-400"
            }`}>
            {o}
            {on && <CheckCircle size={16} weight="fill" className="shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}

function TextBig({ value, onChange, onEnter, placeholder, type = "text", tid, small }) {
  return (
    <input type={type} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter" && onEnter) { e.preventDefault(); onEnter(); } }}
      className={`w-full rounded-lg border border-stone-300 bg-white px-4 ${small ? "py-2.5 text-base" : "py-3.5 text-lg"} text-stone-900 placeholder:text-stone-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`}
      data-testid={tid} />
  );
}

function AreaBig({ value, onChange, tid }) {
  return (
    <textarea rows={4} value={value} placeholder="Type as much or as little as you like"
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-stone-300 bg-white px-4 py-3.5 text-base leading-relaxed text-stone-900 placeholder:text-stone-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      data-testid={tid} />
  );
}
